import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TvAlert, TvButton, TvCard, TvCardBody, TvDiv, TvH5, TvP, TvSmall } from "@trustvid/design-system";
import { useSaasEntitlements } from "../../hooks/useSaasEntitlements";
import { buyCreditsCtaLabel, getPaywallCopy } from "../../constants/saasPlansCatalog";
import { formatMoney, resolveBillingCurrency, resolveMinPaygUnlock } from "../../utils/formatBillingMoney";
import { ROUTE_PATHS } from "../../routes/path-map";
import { buildScopedPath } from "../../utils/account-context";

/**
 * Single upgrade surface for all Free-cap / zero-credit denials.
 */
const UpgradeDrawer = ({
  open = false,
  code = null,
  message = null,
  accountId = null,
  onClose = () => {},
  overlayLayer = "default",
}) => {
  const { entitlements } = useSaasEntitlements(accountId);
  const currency = resolveBillingCurrency(entitlements);
  const minUnlock = resolveMinPaygUnlock(entitlements);
  const minLabel = formatMoney(minUnlock, currency).replace(/\.00$/, "");
  if (!open) return null;
  const isPremium = Boolean(entitlements?.isPremium || entitlements?.isEnterprise);
  const copy = getPaywallCopy(code, { currency, country: entitlements?.country, minUnlock });
  const checkoutPath = accountId
    ? buildScopedPath(accountId, ROUTE_PATHS.buyCredits)
    : ROUTE_PATHS.buyCredits;
  const overlayZIndex = overlayLayer === "modal" ? 2000 : 1050;

  const drawer = (
    <TvDiv
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-md-center justify-content-center"
      style={{ zIndex: overlayZIndex, background: "rgba(15, 23, 42, 0.45)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tv-upgrade-drawer-title"
      data-testid="saas-upgrade-drawer"
      data-overlay-layer={overlayLayer}
    >
      <TvCard className="m-3 m-md-0 w-100" style={{ maxWidth: 440 }}>
        <TvCardBody className="p-4 d-flex flex-column gap-3">
          <TvH5 id="tv-upgrade-drawer-title" className="mb-0">
            {copy.title}
          </TvH5>
          <TvP className="mb-0 text-muted">{copy.body}</TvP>
          {message ? <TvAlert color="light">{message}</TvAlert> : null}
          <TvSmall className="text-muted">
            Minimum commitment {minLabel}
            {currency === "INR" ? " · Razorpay · INR + GST" : " · Razorpay"}
          </TvSmall>
          <TvDiv className="d-flex flex-column flex-sm-row gap-2">
            <TvButton color="primary" tag={Link} to={checkoutPath} onClick={onClose}>
              {buyCreditsCtaLabel({ isPremium, minLabel })}
            </TvButton>
            <TvButton color="secondary" outline type="button" onClick={onClose}>
              Not now
            </TvButton>
          </TvDiv>
        </TvCardBody>
      </TvCard>
    </TvDiv>
  );

  if (typeof document === "undefined" || !document.body) {
    return drawer;
  }
  return createPortal(drawer, document.body);
};

UpgradeDrawer.propTypes = {
  open: PropTypes.bool,
  code: PropTypes.string,
  message: PropTypes.string,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  overlayLayer: PropTypes.oneOf(["default", "modal"]),
};

export default UpgradeDrawer;
